import { DislikeOutlined, LikeOutlined } from '@ant-design/icons'
import { skipToken } from '@reduxjs/toolkit/query'
import { Button, notification, Space, Tooltip } from 'antd'
import compact from 'lodash-es/compact'
import { FunctionComponent, useState } from 'react'
import styled from 'styled-components'
import { useFetchUserStatusQuery } from '@/api/user-api'
import {
  useFetchFeedbackAnalyzeQuery,
  useSubmitAnalyzeFeedbackMutation,
} from '@/api/workspace-api/feedback-api'
import FeedbackNegativeModal from '@/components/modal/feedback-negative-modal'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  FeedbackType,
  SubmitAnalyzeFeedbackRequest,
} from '@/types/api/feedback'
import { Feedback } from '@/types/schema/api/feedback'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import {
  ClickReportAudienceAuthenticityAccuracyProperties,
  ClickReportContentTypeAccuracyProperties,
  ClickReportKolAudienceAccuracyProperties,
  ClickReportSponsoredPostAccuracyProperties,
} from '@/utils/ampli'
import { PlatformShortcode } from '@/utils/convert-platform'
import logger from '@/utils/logger'
import convertModelFileItemToPicture from '@/utils/types/convert-model-file-item-to-picture'

interface AudienceFeedbackProps {
  kolInfo?: KolSchemaModel
  platform: PlatformShortcode
  feedbackType: FeedbackType
  feedbackEvent: (
    props:
      | ClickReportKolAudienceAccuracyProperties
      | ClickReportAudienceAuthenticityAccuracyProperties
      | ClickReportSponsoredPostAccuracyProperties
      | ClickReportContentTypeAccuracyProperties,
  ) => void
}

const getFeedbackAnalyze = (
  feedbackType: FeedbackType,
  feedback: Feedback,
): boolean => {
  switch (feedbackType) {
    case FeedbackType.Audience:
      return feedback.analyze_audience
    case FeedbackType.AudienceAuthenticity:
      return feedback.analyze_audience_authenticity
    case FeedbackType.Advertorial:
      return feedback.analyze_advertorial
    case FeedbackType.CustomizedTag:
      return feedback.analyze_customized_tag
    default:
      return false
  }
}

const AnalyzeFeedback: FunctionComponent<AudienceFeedbackProps> = ({
  kolInfo,
  platform,
  feedbackType,
  feedbackEvent,
}) => {
  const { formatMessage } = useIntl()
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [reported, setReported] = useState(false)

  const currentPlatform = platform
  const { data: userStatus } = useFetchUserStatusQuery()
  const { data: feedbackAnalyze } = useFetchFeedbackAnalyzeQuery(
    kolInfo?.id && userStatus?.currentWorkspaceId && currentPlatform
      ? {
          workspaceId: userStatus.currentWorkspaceId,
          kol_uuid: kolInfo?.id,
          platform_type: currentPlatform,
        }
      : skipToken,
  )
  const [submitAnalyzeFeedback] = useSubmitAnalyzeFeedbackMutation()

  const handleSubmitFeedback = async (
    is_accurate: boolean,
    reason?: string,
    pictures?: SubmitAnalyzeFeedbackRequest['pictures'],
  ): Promise<void> => {
    setFeedbackModalOpen(false)

    try {
      if (!userStatus?.currentWorkspaceId || !kolInfo?.id || !currentPlatform) {
        return
      }

      await submitAnalyzeFeedback({
        is_accurate,
        workspaceId: userStatus.currentWorkspaceId,
        kol_uuid: kolInfo?.id,
        platform_type: currentPlatform,
        reason,
        pictures,
        feedbackType,
      }).unwrap()

      setReported(true)
    } catch (error) {
      logger.error(error)

      notification.error({
        message: formatMessage({ id: 'general:operation_failure' }),
        placement: 'bottomRight',
      })
    }
  }

  if (reported) {
    return (
      <ReportedMessage>
        {formatMessage({ id: 'general:feedback_submit_success' })}
      </ReportedMessage>
    )
  }

  if (!feedbackAnalyze || getFeedbackAnalyze(feedbackType, feedbackAnalyze)) {
    return null
  }

  return (
    <div>
      <Space>
        <Title>{formatMessage({ id: 'general:feedback_title' })}</Title>
        <Tooltip title={formatMessage({ id: 'general:feedback_positive' })}>
          <Button
            icon={<LikeOutlined />}
            size='small'
            onClick={async (): Promise<void> => {
              feedbackEvent({
                isAccurate: true,
                platform,
                kolName: kolInfo?.name,
              })
              await handleSubmitFeedback(true)
            }}
          />
        </Tooltip>
        <Tooltip title={formatMessage({ id: 'general:feedback_negative' })}>
          <Button
            icon={<DislikeOutlined />}
            size='small'
            onClick={(): void => {
              feedbackEvent({
                isAccurate: false,
                platform,
                kolName: kolInfo?.name,
              })
              setFeedbackModalOpen(true)
            }}
          />
        </Tooltip>
      </Space>
      <FeedbackNegativeModal
        open={feedbackModalOpen}
        onCancel={async (): Promise<void> => {
          await handleSubmitFeedback(false)
        }}
        onOk={async (values): Promise<void> => {
          feedbackEvent({ isAccurate: false, platform })
          await handleSubmitFeedback(
            false,
            values.reason,
            compact(
              values.uploadInfo?.fileList.map((file) =>
                file.response
                  ? convertModelFileItemToPicture(file.response)
                  : undefined,
              ),
            ),
          )
        }}
      />
    </div>
  )
}

const Title = styled.div`
  color: ${({ theme }): string => theme.colors.gray8};
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 30px;
`

const ReportedMessage = styled(Title)`
  color: ${({ theme }): string => theme.colors.brand.primary};

  .anticon {
    font-size: 16px;
    margin-right: 4px;
  }
`

export default AnalyzeFeedback
